import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  StickyNote2 as NoteIcon,
  Apartment as BuildingIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { getIncidentDetail, updateIncidentDetails } from '../Services/IncidentService';
import { Incident, UpdateIncidentDetailsRequest } from '../Types/Incident';
import IncidentTypeSelector, { INCIDENT_TYPES } from '../Components/Incidents/IncidentTypeSelector';
import IncidentIcon from '../Components/Incidents/IncidentIcon';
import { appColors } from '../Config/Theme';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import { useSecurity } from '../Contexts/SecurityProvider';
import RequiredStepSectionHeader from '../Components/Elements/RequiredStepSectionHeader';
import { getIncidentTypeLabel } from '../Utils/dashboardHelpers';
import { formatDateTime } from '../Utils/dateHelpers';
import { isIncidentClosed, INCIDENT_EDIT_ROLES } from '../Utils/incidentHelpers';

const NAME_MAX = 256;
const NOTES_MAX = 4000;

const EditIncidentPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useSecurity();

  // ── Data state ──
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notAuthorized, setNotAuthorized] = useState(false);

  // ── Form state ──
  const [incidentType, setIncidentType] = useState('');
  const [incidentName, setIncidentName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Discard dialog ──
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const hasAnyRoleRef = useRef(hasAnyRole);
  hasAnyRoleRef.current = hasAnyRole;

  // ── Load incident ──
  useEffect(() => {
    if (!incidentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const detail = await getIncidentDetail(incidentId);
        if (cancelled) return;
        if (!detail) {
          setLoadError('Incident not found.');
          setLoading(false);
          return;
        }
        // Permission check
        const canEdit = detail.capabilities?.canEditDetails === true
          || hasAnyRoleRef.current(INCIDENT_EDIT_ROLES);
        if (!canEdit) {
          setNotAuthorized(true);
          setLoading(false);
          return;
        }
        setIncident(detail);
        setIncidentType(detail.incidentType ?? '');
        setIncidentName(detail.incidentName ?? '');
        setNotes(detail.notes ?? '');
      } catch (err: unknown) {
        if (!cancelled) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            setLoadError('Incident not found.');
          } else if (status === 403) {
            setNotAuthorized(true);
          } else {
            setLoadError(err instanceof Error ? err.message : 'Failed to load incident.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [incidentId]);

  // ── Derived state ──
  const isClosed = incident ? isIncidentClosed({ status: incident.status }) : false;

  const nameError = useMemo(() => {
    if (incidentName.trim().length === 0) return 'Incident name is required.';
    if (incidentName.length > NAME_MAX) return `Name must be ${NAME_MAX} characters or fewer.`;
    return null;
  }, [incidentName]);

  const notesError = useMemo(() => {
    if (notes.length > NOTES_MAX) return `Notes must be ${NOTES_MAX} characters or fewer.`;
    return null;
  }, [notes]);

  const isValid = !nameError && !notesError && (!isClosed || incidentType.length > 0);

  const hasChanges = useMemo(() => {
    if (!incident) return false;
    return (
      incidentName !== (incident.incidentName ?? '') ||
      incidentType !== (incident.incidentType ?? '') ||
      notes !== (incident.notes ?? '')
    );
  }, [incident, incidentName, incidentType, notes]);

  const canSave = isValid && hasChanges && !submitting;

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    if (!canSave || !incident || !incidentId) return;
    setSubmitting(true);
    setSubmitError(null);

    const request: UpdateIncidentDetailsRequest = {
      incidentName: incidentName.trim(),
      ...(incidentType ? { incidentType } : {}),
      notes: notes.trim(),
      expectedUpdatedDate: incident.updatedDate ?? null,
    };

    try {
      const result = await updateIncidentDetails(incidentId, request);
      // Update local state so dirty check resets — preserve locally-entered
      // values when the API response omits them (e.g. notes).
      if (result.incident) {
        setIncident({ ...result.incident, notes: result.incident.notes ?? request.notes ?? null });
        setIncidentName(result.incident.incidentName ?? incidentName);
        setIncidentType(result.incident.incidentType ?? incidentType);
        setNotes(result.incident.notes ?? request.notes ?? '');
      }
      navigate(`/incidents/${incidentId}/incidentdashboard`, {
        state: { snackbar: 'Incident details updated.' },
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setSubmitError('You do not have permission to edit this incident.');
      } else if (status === 409) {
        setSubmitError('This incident was updated by someone else. Refresh and try again.');
        // Refetch to get latest
        try {
          const detail = await getIncidentDetail(incidentId);
          if (detail) {
            setIncident(detail);
            setIncidentType(detail.incidentType ?? '');
            setIncidentName(detail.incidentName ?? '');
            setNotes(detail.notes ?? '');
          }
        } catch { /* ignore refetch error */ }
      } else {
        const apiMsg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        setSubmitError(apiMsg || (err instanceof Error ? err.message : 'Failed to update incident details.'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [canSave, incident, incidentId, incidentName, incidentType, notes, navigate]);

  // ── Navigation with dirty check ──
  const navigateWithDirtyCheck = useCallback((path: string) => {
    if (hasChanges) {
      setPendingNavigation(path);
      setDiscardDialogOpen(true);
    } else {
      navigate(path);
    }
  }, [hasChanges, navigate]);

  const handleCancel = useCallback(() => {
    const backPath = incidentId
      ? `/incidents/${incidentId}/incidentdashboard`
      : '/';
    navigateWithDirtyCheck(backPath);
  }, [incidentId, navigateWithDirtyCheck]);

  const handleDiscardConfirm = useCallback(() => {
    setDiscardDialogOpen(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate]);

  // ── Loading state ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2, color: appColors.text.body }}>Loading incident…</Typography>
      </Box>
    );
  }

  // ── Not authorized ──
  if (notAuthorized) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2, justifyContent: 'center' }}>
          You do not have permission to edit this incident.
        </Alert>
        <Button variant="contained" onClick={() => navigate(incidentId ? `/incidents/${incidentId}/incidentdashboard` : '/')}>
          Go Back
        </Button>
      </Box>
    );
  }

  // ── Load error ──
  if (loadError || !incident) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2, justifyContent: 'center' }}>{loadError || 'Incident not found.'}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}>Back to Incidents</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
        {/* Breadcrumbs */}
        <AppBreadcrumbs items={[
          { label: 'Incidents', path: '/' },
          { label: incident.incidentName || 'Incident', path: `/incidents/${incidentId}/incidentdashboard` },
          { label: 'Edit' },
        ]} />

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: appColors.text.heading }}>
              Edit Incident
            </Typography>
            <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
              Update incident type, incident name, and notes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={!canSave}
            sx={{
              minHeight: 40,
              fontSize: '0.875rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </Box>

        {/* Incident summary bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            mb: 2,
            borderRadius: '10px',
            bgcolor: appColors.surface.card,
            border: `1px solid ${appColors.border.default}`,
            flexWrap: 'wrap',
          }}
        >
          <IncidentIcon incidentType={incident.incidentType} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>
            {getIncidentTypeLabel(incident.incidentType) ?? incident.incidentType}
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.caption }}>·</Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body }}>
            {incident.incidentName}
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.caption }}>·</Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body }}>
            {incident.location}
          </Typography>
          {incident.startedAtUtc && (
            <>
              <Typography variant="body2" sx={{ color: appColors.text.caption }}>·</Typography>
              <Typography variant="body2" sx={{ color: appColors.text.caption }}>
                Started {formatDateTime(incident.startedAtUtc)}
              </Typography>
            </>
          )}
          <Chip
            label={isClosed ? 'Closed' : 'Active'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              bgcolor: isClosed ? `${appColors.text.caption}20` : `${appColors.status.success}18`,
              color: isClosed ? appColors.text.caption : appColors.status.success,
            }}
          />
        </Box>

        {/* Info banners */}
        <Alert severity="info" sx={{ borderRadius: '8px', mb: 1 }}>
          Only incident type, incident name, and notes can be changed. Headcount, roster, location, and status are not affected.
        </Alert>
        {isClosed && (
          <Alert severity="info" sx={{ borderRadius: '8px', mb: 1 }}>
            This incident is closed. Editing details will not reopen the incident or change final headcount results.
          </Alert>
        )}

        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

        {/* Section 1: Incident Type */}
        <RequiredStepSectionHeader
          stepNumber={1}
          title="Incident Type"
          subtitle={isClosed ? 'Select the incident type.' : 'Set the type now or when the incident is closed.'}
          required={isClosed}
          isComplete={Boolean(incidentType)}
        />
        <IncidentTypeSelector value={incidentType} onChange={setIncidentType} />

        <Divider sx={{ my: 3 }} />

        {/* Section 2: Incident Details */}
        <RequiredStepSectionHeader
          stepNumber={2}
          title="Incident Details"
          subtitle="Update the incident name and notes."
          required
          isComplete={Boolean(incidentName.trim())}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          {/* Incident Name */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Incident Name <Typography component="span" sx={{ color: appColors.status.error }}>*</Typography>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={incidentName}
              onChange={(e) => setIncidentName(e.target.value)}
              error={incidentName.length > 0 && !!nameError}
              helperText={
                incidentName.length > 0 && nameError
                  ? nameError
                  : `${incidentName.length}/${NAME_MAX}`
              }
              slotProps={{
                htmlInput: { maxLength: NAME_MAX + 1 },
              }}
              disabled={submitting}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Box>

          {/* Location (read-only) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Location
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={incident.location ?? ''}
              disabled
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <BuildingIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <LockIcon sx={{ fontSize: 14, color: appColors.text.disabled }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <Typography variant="caption" sx={{ color: appColors.text.caption, mt: 0.5, display: 'block' }}>
              Location cannot be changed after incident creation.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
          Notes{' '}
          <Typography component="span" variant="caption" sx={{ color: appColors.text.placeholder }}>(optional)</Typography>
        </Typography>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              border: `1px solid ${notesError ? appColors.status.error : appColors.border.default}`,
              borderRadius: '8px',
              bgcolor: appColors.surface.card,
              minHeight: 120,
              '&:hover': { borderColor: notesError ? appColors.status.error : appColors.border.hover },
              '&:focus-within': { borderColor: notesError ? appColors.status.error : appColors.primary.main, borderWidth: 2 },
            }}
          >
            <Box sx={{ pl: 1.5, pt: 1.5, flexShrink: 0 }}>
              <NoteIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
            </Box>
            <textarea
              placeholder="Add relevant instructions, corrections, or post-incident notes…"
              value={notes}
              onChange={(e) => { if (e.target.value.length <= NOTES_MAX) setNotes(e.target.value); }}
              rows={5}
              disabled={submitting}
              aria-label="Notes"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                resize: 'none',
                padding: '12px 12px 24px 8px',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: appColors.text.heading,
                backgroundColor: 'transparent',
                opacity: submitting ? 0.6 : 1,
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 8,
              color: notesError ? appColors.status.error : appColors.text.placeholder,
            }}
          >
            {notes.length} / {NOTES_MAX}
          </Typography>
          {notesError && (
            <Typography variant="caption" sx={{ color: appColors.status.error, mt: 0.5, display: 'block' }}>
              {notesError}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Bottom action bar */}
      <Box
        sx={{
          borderTop: `1px solid ${appColors.border.default}`,
          bgcolor: appColors.surface.card,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={submitting}
          sx={{
            flex: 1,
            maxWidth: 280,
            minHeight: 48,
            fontSize: '0.9375rem',
            color: appColors.text.heading,
            borderColor: appColors.border.subtle,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={!canSave}
          sx={{
            flex: 1,
            maxWidth: 280,
            minHeight: 48,
            fontSize: '0.9375rem',
            fontWeight: 700,
          }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      {/* Discard changes dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '12px' } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Discard changes?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: appColors.text.body }}>
            You have unsaved changes to this incident.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDiscardDialogOpen(false)} color="inherit">
            Keep Editing
          </Button>
          <Button variant="contained" color="error" onClick={handleDiscardConfirm}>
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditIncidentPage;
